import { type MouseEvent, useCallback, useEffect, useState } from "react";

interface ContextMenuState {
  anchorPosition: { top: number; left: number } | null;
  open: boolean;
  contextMenuEvent?: MouseEvent;
}

const contextMenuInitialState: ContextMenuState = {
  anchorPosition: null,
  open: false,
  contextMenuEvent: undefined,
};

export const useContextMenu = () => {
  const [contextMenu, setContextMenu] = useState<ContextMenuState>(
    contextMenuInitialState,
  );

  const openContextMenu = useCallback((event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    // Close any existing context menu first
    setContextMenu({
      anchorPosition: null,
      open: false,
      contextMenuEvent: undefined,
    });

    // Use requestAnimationFrame to ensure the close happens before opening new one
    requestAnimationFrame(() => {
      setContextMenu({
        anchorPosition: {
          top: event.clientY,
          left: event.clientX,
        },
        open: true,
        contextMenuEvent: event,
      });
    });
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextMenu({
      anchorPosition: null,
      open: false,
      contextMenuEvent: undefined,
    });
  }, []);

  // Close custom context menu when native context menu tries to open
  useEffect(() => {
    const handleGlobalContextMenu = (event: Event) => {
      if (contextMenu.open) {
        event.preventDefault();
        closeContextMenu();
      }
    };

    if (contextMenu.open) {
      document.addEventListener("contextmenu", handleGlobalContextMenu, true);
      return () => {
        document.removeEventListener(
          "contextmenu",
          handleGlobalContextMenu,
          true,
        );
      };
    }
  }, [contextMenu.open, closeContextMenu]);

  return {
    contextMenuProps: {
      anchorPosition: contextMenu.anchorPosition,
      open: contextMenu.open,
      onClose: closeContextMenu,
      contextMenuEvent: contextMenu.contextMenuEvent,
    },
    openContextMenu,
    closeContextMenu,
  };
};
